import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../../stores/workbench';
import { getHeadlessTemplateFiles } from '../../templates/headlessTemplate';
import { materializeHotEditBridgeFilesInStore } from '../../utils/mergedFiles';
import { normalizeWorkbenchPath } from '../../utils/normalizeWorkbenchPath';
import { cleanLlmContent, containsJsx, autoFixImports } from './llm-content-fixes';
import {
  APP_STORE_PATH,
  STARTUP_SAMPLES_PATH,
  applyAppDataConventions,
  sharedEntitiesFromSamples,
} from './appDataConventions';
import { platformFileRefusal } from './platformFiles';
import { executePrune } from './pruneWorkspace';

export function resolveLogoToken(content: string, logoSrc?: string): string {
  if (!logoSrc || !content.includes('__BRAND_LOGO_SRC__')) return content;
  return content.replace(/__BRAND_LOGO_SRC__/g, logoSrc);
}

/** Store key for a user-edited file that would be overwritten by an AI write. */
function findManualEditConflict(
  resolvedPath: string,
  originalPath: string,
): { path: string; userContent: string } | null {
  const files = workbenchStore.files.get();
  for (const candidate of [resolvedPath, originalPath]) {
    const normalized = normalizeWorkbenchPath(candidate);
    if (!normalized) continue;
    const entry = files[normalized];
    if (entry?.modified) {
      return { path: normalized, userContent: entry.content };
    }
  }
  return null;
}

export async function resolveFileConflict(
  webcontainer: WebContainer | null,
  filePath: string,
  resolution: 'accept' | 'reject',
): Promise<void> {
  const trimmed = normalizeWorkbenchPath(filePath);
  if (!trimmed) return;
  const conflict = workbenchStore.fileConflicts.get()[trimmed];
  if (!conflict) return;

  const targetPath = normalizeWorkbenchPath(conflict.aiPath ?? trimmed) ?? trimmed;
  const dir = targetPath.includes('/') ? targetPath.substring(0, targetPath.lastIndexOf('/')) : '';
  if (dir) {
    await webcontainer?.fs.mkdir(dir, { recursive: true });
  }

  if (resolution === 'accept') {
    await webcontainer?.fs.writeFile(targetPath, conflict.aiContent);
    workbenchStore.recordToolWriteSnapshot(targetPath, conflict.aiContent);
    workbenchStore.files.setKey(targetPath, { content: conflict.aiContent, modified: false });
    if (targetPath !== trimmed) {
      try {
        await webcontainer?.fs.rm(trimmed);
      } catch {
        /* orphan path may not exist */
      }
      workbenchStore.removeFile(trimmed);
    }
  } else {
    await webcontainer?.fs.writeFile(trimmed, conflict.userContent);
    workbenchStore.files.setKey(trimmed, { content: conflict.userContent, modified: true });
  }

  workbenchStore.clearFileConflict(trimmed);
}

export async function executeWriteFile(
  webcontainer: WebContainer | null,
  filePath: string,
  content: string,
  signal?: AbortSignal,
): Promise<string> {
  let cleaned = cleanLlmContent(content);

  const normalizedInput = normalizeWorkbenchPath(filePath);
  if (!normalizedInput) {
    return 'Error: invalid path';
  }

  let resolvedPath = normalizedInput;
  if (/\.ts$/i.test(resolvedPath) && containsJsx(cleaned)) {
    resolvedPath = resolvedPath.replace(/\.ts$/i, '.tsx');
  } else if (/\.js$/i.test(resolvedPath) && containsJsx(cleaned)) {
    resolvedPath = resolvedPath.replace(/\.js$/i, '.jsx');
  }

  if (/\.(tsx?|jsx?)$/i.test(resolvedPath)) {
    const workspace = workbenchStore.files.get();
    if (workspace[APP_STORE_PATH] && resolvedPath !== APP_STORE_PATH && resolvedPath !== STARTUP_SAMPLES_PATH) {
      cleaned = applyAppDataConventions(
        cleaned,
        sharedEntitiesFromSamples(workspace[STARTUP_SAMPLES_PATH]?.content),
      ).content;
    }
    const fixed = autoFixImports(cleaned);
    cleaned = fixed.content;
  }

  const resolved = resolveLogoToken(cleaned, workbenchStore.brandLogoSrc.get());

  const refusal = platformFileRefusal(resolvedPath, workbenchStore.files.get());
  if (refusal) return refusal;

  const manualConflict = findManualEditConflict(resolvedPath, normalizedInput);
  if (manualConflict) {
    workbenchStore.setFileConflict(manualConflict.path, {
      aiContent: resolved,
      userContent: manualConflict.userContent,
      aiPath: resolvedPath !== manualConflict.path ? resolvedPath : undefined,
    });
    return (
      `Conflict: ${manualConflict.path} has manual edits — kept your version. ` +
      `Review the diff in the editor and accept or reject the AI changes.`
    );
  }

  const existingEntry = workbenchStore.files.get()[resolvedPath];
  if (existingEntry && !existingEntry.modified && existingEntry.content === resolved) {
    return `No change: ${resolvedPath} already has this exact content. Do not rewrite it — call build.`;
  }

  if (signal?.aborted) {
    return `Skipped: turn was cancelled before ${resolvedPath} could be written.`;
  }

  const dir = resolvedPath.includes('/') ? resolvedPath.substring(0, resolvedPath.lastIndexOf('/')) : '';
  if (dir) {
    await webcontainer?.fs.mkdir(dir, { recursive: true });
  }

  if (resolvedPath !== normalizedInput && /\.tsx?$/i.test(normalizedInput)) {
    try {
      await webcontainer?.fs.rm(normalizedInput);
      workbenchStore.removeFile(normalizedInput);
    } catch {
      /* orphan may not exist */
    }
  }

  await webcontainer?.fs.writeFile(resolvedPath, resolved);

  workbenchStore.recordToolWriteSnapshot(resolvedPath, resolved);
  workbenchStore.files.setKey(resolvedPath, { content: resolved, modified: false });

  return `Wrote ${resolvedPath} (${resolved.length} bytes)`;
}

export async function persistUserFile(
  webcontainer: WebContainer | null,
  filePath: string,
  content: string,
  modified: boolean = false,
  signal?: AbortSignal,
): Promise<void> {
  const normalized = normalizeWorkbenchPath(filePath);
  if (!normalized) return;

  const existing = workbenchStore.files.get()[normalized];
  if (!modified && existing?.content === content) {
    return;
  }

  if (signal?.aborted) return;

  const dir = normalized.includes('/') ? normalized.substring(0, normalized.lastIndexOf('/')) : '';
  if (dir) {
    await webcontainer?.fs.mkdir(dir, { recursive: true });
  }
  if (signal?.aborted) return;
  await webcontainer?.fs.writeFile(normalized, content);
  workbenchStore.files.setKey(normalized, { content, modified });
  if (!modified) {
    workbenchStore.recordToolWriteSnapshot(normalized, content);
  }
}

export async function flushDirtyEditorFilesToWebContainer(
  webcontainer: WebContainer | null,
): Promise<number> {
  const dirty = workbenchStore.getModifiedFiles();
  const paths = Object.keys(dirty);
  for (const path of paths) {
    await persistUserFile(webcontainer, path, dirty[path]!, true);
  }
  return paths.length;
}

export async function flushModifiedFilesToWebContainer(
  webcontainer: WebContainer | null,
): Promise<number> {
  return flushDirtyEditorFilesToWebContainer(webcontainer);
}

export async function flushDirtyEditorFilesBeforeStream(
  webcontainer: WebContainer | null,
): Promise<number> {
  return flushDirtyEditorFilesToWebContainer(webcontainer);
}

/**
 * Reset WebContainer + store to the pristine headless template.
 * Used when switching conversations with no snapshot.
 */
export async function resetContainerToTemplate(webcontainer: WebContainer | null): Promise<void> {
  const files = getHeadlessTemplateFiles();
  const paths = Object.keys(files);

  workbenchStore.files.set({});
  workbenchStore.assets.set({});
  workbenchStore.lastToolWrittenByPath.set({});
  workbenchStore.fileConflicts.set({});
  // The store no longer mirrors every file in the container; the next restore writes them all.
  workbenchStore.filesTrustedFor.set(null);
  workbenchStore.snapshotPreviewReady.set(false);
  workbenchStore.buildStatus.set('idle');
  workbenchStore.hasUnbuiltEdits.set(false);

  const dirs = new Set<string>();
  for (const filePath of paths) {
    const dir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
    if (dir) dirs.add(dir);
  }
  const sortedDirs = [...dirs].sort((a, b) => a.split('/').length - b.split('/').length);
  for (const d of sortedDirs) {
    await webcontainer?.fs.mkdir(d, { recursive: true });
  }
  for (const filePath of paths) {
    await webcontainer?.fs.writeFile(filePath, files[filePath]);
    workbenchStore.files.setKey(filePath, { content: files[filePath], modified: false });
    workbenchStore.recordToolWriteSnapshot(filePath, files[filePath]);
  }

  // The previous conversation may have added files under src/ that aren't part of the
  // pristine template (e.g. a generated page) — those never get removed by the writes
  // above, so they'd otherwise persist into the new conversation's WebContainer and can
  // affect its first build. `executePrune`'s own preserve set already covers every
  // template path, so no extra `keep` list is needed here.
  await executePrune(webcontainer, []);

  materializeHotEditBridgeFilesInStore();
}
